/* eslint-disable lingui/no-unlocalized-strings -- metadados técnicos de fonte e caminhos da API (U40) */
import { z } from 'zod';
import { defineEntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http';
import { defineDataSource } from '@/shared/page';
import { auditQuery, getAudit } from '@/shared/reference';
import { auditEntrySchema } from '@/shared/reference/reference.wire';
import {
  listPayrollAgreements,
  getPayrollAgreement,
  listEmployeePositions,
} from './payroll-agreement.api';
import { payrollAgreementFilters } from './payroll-agreement.filters';
import { payrollAgreementKeys } from './payroll-agreement.queries';
import { payrollAgreementSchema } from './payroll-agreement.wire';
export const payrollAgreementListParams = z.object({
  page: z.number().int().min(0).default(0),
  size: z.number().int().min(1).max(100).default(5),
  filters: z.record(z.string(), z.string()).default({}),
});
export const toWireListParams = (p: z.output<typeof payrollAgreementListParams>) => ({
  page: p.page,
  size: p.size,
  ...payrollAgreementFilters.toParams(p.filters),
  tenant: getCurrentTenant() ?? p.filters['tenant'],
});
export const payrollAgreementListSource = defineDataSource({
  id: 'payrollAgreement.list',
  description:
    'Payroll agreement list with correspondent, name, registration office and creation date.',
  shape: 'page',
  params: payrollAgreementListParams,
  response: payrollAgreementSchema,
  permission: 'none',
  reason: 'The legacy Agreement routes and list do not require Read permission (router.tsx:1842).',
  sensitivity: 'internal',
  limits: { staleTimeMs: 30_000, maxSize: 20 },
  key: (p) => payrollAgreementKeys.list(toWireListParams(p)),
  fetch: async (p, { signal }) => {
    const r = await listPayrollAgreements(toWireListParams(p), signal);
    return { items: r.data, total: r.totalItems, pageCount: r.totalPages };
  },
});
export const recordParams = z.object({ id: z.string().min(1) });
export const payrollAgreementRecordSource = defineDataSource({
  id: 'payrollAgreement.record',
  description: 'Payroll agreement record with registration settings, dates and operator names.',
  shape: 'record',
  params: recordParams,
  response: payrollAgreementSchema,
  permission: 'none',
  reason: 'The legacy agreement detail has no Read permission guard (router.tsx:1852).',
  sensitivity: 'restricted',
  limits: { staleTimeMs: 30_000 },
  key: ({ id }) => payrollAgreementKeys.detail(id),
  fetch: ({ id }, { signal }) => getPayrollAgreement(id, signal),
});
export const payrollAgreementHistorySource = defineDataSource({
  id: 'payrollAgreement.history',
  description:
    'Audit history of payroll agreement settings, available only in the root tenant form.',
  shape: 'series',
  params: recordParams,
  response: auditEntrySchema,
  permission: 'none',
  reason: 'The legacy audit drawer checks root tenancy in the form, without CRUD permission.',
  sensitivity: 'restricted',
  limits: { staleTimeMs: 30_000 },
  key: ({ id }) => auditQuery('PayrollAgreement', id).queryKey,
  fetch: ({ id }, { signal }) => getAudit('PayrollAgreement', id, signal),
});
export const payrollAgreementSource = defineEntitySource({
  key: payrollAgreementKeys.custom('options'),
  scope: () => getCurrentTenant() ?? null,
  search: async (searchString, signal) =>
    (
      await listPayrollAgreements(
        { searchString: searchString || undefined, tenant: getCurrentTenant() ?? undefined },
        signal,
      )
    ).data,
  byId: getPayrollAgreement,
  toOption: (item) => ({ value: item.id, label: item.name }),
});
export const employeePositionSource = defineEntitySource({
  key: payrollAgreementKeys.custom('cbo'),
  search: listEmployeePositions,
  toOption: (item) => ({ value: item.code, label: `${item.code} - ${item.description}` }),
});
